/**
 * Pointer-space helpers for the app-wide UI scale (`zoom` on <html>, see
 * stores/uiScale.ts).
 *
 * Under CSS `zoom`, two coordinate spaces coexist and only ONE mix of them
 * is wrong. Viewport space — `event.clientX`, `getBoundingClientRect()`,
 * `window.innerWidth` — is unaffected by zoom and self-consistent, so
 * hit-testing a pointer against a rect stays correct. An element's OWN CSS
 * pixels — `clientWidth`, a `width:` you set, a `translate()` you apply —
 * are the zoomed unit: 100 of them paint as 125 viewport px at Normal.
 * The bug class is therefore "a viewport delta written back as a CSS-px
 * length": a sidebar dragged 100 viewport px grows by 125, an image
 * outruns the finger dragging it, a canvas drawn in clientWidth space is
 * hit-tested with clientX. Divide those deltas by the element's zoom.
 *
 * `Element.currentCSSZoom` (Chrome 128+) is the browser's own answer; the
 * fallback reads the root scale variable for engines that lack it, which
 * is correct everywhere except inside a `.ui-scale-reset` subtree.
 */
export function cssZoomOf(el?: Element | null): number {
	const target = (el ?? document.documentElement) as Element & { currentCSSZoom?: number };
	const z = target.currentCSSZoom;
	if (typeof z === "number" && Number.isFinite(z) && z > 0) return z;
	const raw = getComputedStyle(document.documentElement).getPropertyValue("--ui-scale");
	const parsed = Number.parseFloat(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

/** A pointer/rect distance measured in viewport px, expressed in `el`'s own CSS px. */
export function toCssPx(viewportPx: number, el?: Element | null): number {
	return viewportPx / cssZoomOf(el);
}
